import { ElementRef, Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class InViewService {
  private elementSubjects = new Map<Element, BehaviorSubject<boolean>>();
  private intersectionObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const subject = this.elementSubjects.get(entry.target);
      if (subject) {
        subject.next(entry.isIntersecting);
      }
    });
  });

  private getElement(element: ElementRef) {
    if (element.nativeElement instanceof HTMLElement) {
      return element.nativeElement;
    }
    return element.nativeElement.parentElement;
  }

  observe(element: ElementRef<HTMLElement>): Observable<boolean> {
    const el = this.getElement(element);

    // Return a new Observable for the element
    return new Observable<boolean>(subscriber => {
      // If the element is not already observed, add it
      if (!this.elementSubjects.has(el)) {
        const subject = new BehaviorSubject<boolean>(false);
        this.elementSubjects.set(el, subject);
        this.intersectionObserver.observe(el);
      }

      // Subscribe to the subject's observable
      const subject = this.elementSubjects.get(el)!;
      const subscription = subject.asObservable().pipe(distinctUntilChanged()).subscribe(subscriber);

      // Teardown logic to unobserve the element when unsubscribing
      return () => {
        subscription.unsubscribe();
        if (this.elementSubjects.has(el)) {
          this.intersectionObserver.unobserve(el);
          this.elementSubjects.get(el)?.complete();
          this.elementSubjects.delete(el);
        }
      };
    });
  }
}
